/**
 * The app's one "major/destructive" toast shape: icon disc + title +
 * one-line supporting text + dismiss, self-portaled to document.body
 * bottom-left — not routed through react-notifications-component, whose
 * bottom-left container has a real height-sync bug (outer container ends up
 * inline height:0 while its child carries the real height, pushing the
 * toast below the viewport). Position/sizing per
 * design_handoff_workspace_members FIXES2 item 9, "the product's one toast
 * anchor" — the canonical spec this used to be copy-pasted from into
 * DomainToast, DeleteToast, the CustomDomainPage DomainToast and
 * MemberRemovedToast (each slightly drifted); those now all use this.
 * For short/reversible notices use theme/untitled/Toast (bottom-center, no
 * title) instead — the two are deliberately different, not interchangeable.
 *
 * @param {string=CheckCircle} icon
 * @param {string} title
 * @param {string=} text - one-line supporting copy
 * @param {number=} duration - ms until auto-dismiss; omit to require manual dismiss
 * @param {function} onDismiss
 */
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import IconButton from "../../theme/untitled/IconButton/IconButton";
import Icon from "../../theme/untitled/icons/Icon";
import styles from "./MajorToast.module.scss";

const MajorToast = ({ icon = "CheckCircle", title, text, duration, onDismiss }) => {
  useEffect(() => {
    if (!duration) {
      return undefined;
    }

    const timer = setTimeout(onDismiss, duration);
    return () => clearTimeout(timer);
  }, [onDismiss, duration]);

  return createPortal(
    <div className={styles.root} role="status">
      <span className={styles.disc}>
        <Icon name={icon} size={20} />
      </span>
      <div className={styles.body}>
        <p className={styles.title}>{title}</p>
        {text && <p className={styles.text}>{text}</p>}
      </div>
      <span className={styles.dismiss}>
        <IconButton size="sm" variant="ghost" icon={<Icon name="XClose" />} label="Dismiss" onClick={onDismiss} />
      </span>
    </div>,
    document.body
  );
};

export default MajorToast;
